import type { ScreenerTickerData } from '@/types'
import { historyWindowCovers, SCREENER_INTERVAL_SECONDS, type ChartTimeRange } from './screenerHistory'
import { canonicalExchangeScope } from './screenerPriceExchange'
import { completedHistoryRanges, mergeProgressiveOI, pendingHistoryRanges } from './screenerProgressiveHistory'

export type ScreenerHistoryScope = { symbol: string; exchanges: string[]; markets?: string[];
  metrics?: string[]; includeOIOhlc: boolean }

const sorted = (values: string[] | undefined) => values?.map(value => value.trim().toLowerCase()).sort()
const effectiveInterval = (interval: string, range: ChartTimeRange) => Object.keys(SCREENER_INTERVAL_SECONDS)
  .find(value => SCREENER_INTERVAL_SECONDS[value] >= (SCREENER_INTERVAL_SECONDS[interval] || 60)
    && Math.ceil((range.to - range.from) / SCREENER_INTERVAL_SECONDS[value]) + 1 <= 5000) || interval

/** Rebase only coverage metadata. Values retain their real resolution and CVD
 * baseline; missing ranges remain pending rather than becoming empty history. */
export function rebaseScreenerHistory(data: ScreenerTickerData, range: ChartTimeRange): ScreenerTickerData {
  if (!data.history_window || data.oi_interval === '5s') return data
  const step = SCREENER_INTERVAL_SECONDS[data.oi_interval || '1m'] || 60
  const requested = { from_ts: Math.floor(range.from / step) * step, to_ts: Math.ceil((range.to + 1) / step) * step }
  const completed = completedHistoryRanges(data.oi_history_load?.completed || [{
    from_ts: data.history_window.from_ts, to_ts: data.history_window.to_ts + 1,
  }], requested)
  const pending = pendingHistoryRanges(requested, completed)
  return { ...data, history_window: { ...data.history_window, from_ts: requested.from_ts, to_ts: requested.to_ts - 1 },
    oi_history_load: { ...data.oi_history_load, requested, completed, pending,
      status: pending.length ? 'loading' : 'complete', error: undefined, split_required: undefined, failed: undefined } }
}

type Entry = {
  scope: string
  data: ScreenerTickerData
  range: ChartTimeRange
  requestedSpan: number
  insertedAt: number
  points: number
  bytes: number
  reuseScope?: ScreenerHistoryScope
}

function historyCost(data: ScreenerTickerData): { points: number; bytes: number } {
  let points = 0
  let bytes = 1024
  for (const exchange of Object.values(data.exchanges)) {
    bytes += 2048
    for (const series of [exchange.oi_history, exchange.volume_history, exchange.trade_price_history,
      exchange.candles, exchange.cvd_history]) {
      if (!series?.length) continue
      points += series.length
      const samples = [series[0], series[Math.floor(series.length / 2)], series[series.length - 1]]
      bytes += series.length * (64 + Math.max(...samples.map(point => JSON.stringify(point).length * 2)))
    }
  }
  return { points, bytes }
}

function mergeCachedAuthorities(existing: ScreenerTickerData, incoming: ScreenerTickerData,
  target: ScreenerHistoryScope, source: ScreenerHistoryScope): ScreenerTickerData {
  if (existing === incoming || existing.symbol.toUpperCase() !== incoming.symbol.toUpperCase()
    || existing.oi_interval !== incoming.oi_interval || existing.oi_revision_epoch !== incoming.oi_revision_epoch
    || target.includeOIOhlc !== source.includeOIOhlc
    || JSON.stringify(sorted(target.markets)) !== JSON.stringify(sorted(source.markets))) return existing
  const authority = incoming.oi_history_authorities || (incoming.oi_history_load?.completed || []).map(range => ({
    ...range, read_started_at: incoming.read_started_at || 0, exchanges: canonicalExchangeScope(source.exchanges),
  }))
  const groups = new Map<string, typeof authority>()
  for (const value of authority) {
    // Equal versions need no propagation; same-request points are immutable.
    if (existing.oi_history_authorities?.some(previous => previous.read_started_at >= value.read_started_at
      && previous.from_ts <= value.from_ts && previous.to_ts >= value.to_ts
      && canonicalExchangeScope(value.exchanges).every(venue => canonicalExchangeScope(previous.exchanges).includes(venue)))) continue
    const key = JSON.stringify([value.read_started_at, value.exchanges])
    groups.set(key, [...(groups.get(key) || []), value])
  }
  const window = existing.history_window
  if (!window) return existing
  const requested = existing.oi_history_load?.requested || { from_ts: window.from_ts, to_ts: window.to_ts + 1 }
  let result = existing
  for (const ranges of groups.values()) {
    const venues = canonicalExchangeScope(ranges[0].exchanges).filter(venue => canonicalExchangeScope(target.exchanges).includes(venue))
    if (!venues.length) continue
    const baseline = result.oi_history_load ? result : { ...result, oi_history_load: {
      status: 'complete' as const, requested, completed: [requested], pending: [],
    } }
    result = mergeProgressiveOI(baseline, { ...incoming, oi_read_started_at: ranges[0].read_started_at,
      oi_history_load: { status: 'complete', requested, completed: ranges, pending: [] } }, venues)
    // A correction for one venue cannot certify missing history for every
    // venue in a broader cached request. Values and tombstones still propagate.
    if (!canonicalExchangeScope(target.exchanges).every(venue => venues.includes(venue))) {
      result = { ...result, oi_history_load: baseline.oi_history_load }
    }
  }
  return result
}

/** Per-workspace LRU, bounded independently of the number of visited windows.
 * Immutable response snapshots are shared with the displayed chart. The byte
 * budget estimates retained objects from each series' sampled point shape;
 * the independent point cap avoids serializing a whole week on each live tick.
 */
export class ScreenerHistoryCache {
  private entries: Entry[] = []
  private epochs = new Map<string, { value: string; readAt: number }>()
  private appliedAuthorities = new WeakSet<object>()
  // A seven-day/5m response for twelve venues and all four history metrics has
  // about 97k points; keep one complete week while still bounding memory.
  constructor(private limits = { windows: 8, points: 120_000, bytes: 64 * 1024 * 1024, ageMs: 300_000 }) {}

  observeEpoch(data: ScreenerTickerData): void {
    if (!data.oi_revision_epoch) return
    const symbol = data.symbol.toUpperCase()
    if (this.epochs.get(symbol)?.value !== data.oi_revision_epoch) this.invalidate(symbol)
    this.epochs.set(symbol, { value: data.oi_revision_epoch,
      readAt: Math.max(this.epochs.get(symbol)?.readAt || 0, data.read_started_at || 0, data.oi_read_started_at || 0) })
  }

  acceptsEpoch(data: ScreenerTickerData): boolean {
    const known = this.epochs.get(data.symbol.toUpperCase())
    return !known || data.oi_revision_epoch === known.value
  }

  invalidate(symbol: string, from?: number | null): void {
    this.entries = this.entries.filter(entry => entry.data.symbol.toUpperCase() !== symbol.toUpperCase()
      || (from != null && entry.range.to < from))
  }

  get(scope: string, range: ChartTimeRange, interval: string): ScreenerTickerData | null {
    const now = Date.now()
    this.entries = this.entries.filter(entry => now - entry.insertedAt <= this.limits.ageMs)
    const index = this.entries.findIndex(entry => entry.scope === scope
      && (!entry.data.oi_history_load || entry.data.oi_history_load.status === 'complete')
      && historyWindowCovers(entry.range, range)
      && (entry.data.oi_interval === interval || range.to - range.from >= entry.requestedSpan / 2))
    if (index < 0) return null
    const [entry] = this.entries.splice(index, 1)
    this.entries.unshift(entry)
    return entry.data
  }

  reusable(scope: ScreenerHistoryScope, range: ChartTimeRange, interval: string,
    epoch?: string | null, maxAgeMs = this.limits.ageMs): { data: ScreenerTickerData; missingMetrics: string[] } | null {
    const knownEpoch = this.epochs.get(scope.symbol.toUpperCase())?.value
    if (epoch && knownEpoch && epoch !== knownEpoch) return null
    epoch ||= knownEpoch
    const requestedVenues = canonicalExchangeScope(scope.exchanges)
    const resolution = effectiveInterval(interval, range)
    const candidates = this.entries.filter(entry => {
      const source = entry.reuseScope
      return source && Date.now() - entry.insertedAt <= maxAgeMs
        && source.symbol.toUpperCase() === scope.symbol.toUpperCase() && source.includeOIOhlc === scope.includeOIOhlc
        && JSON.stringify(sorted(source.markets)) === JSON.stringify(sorted(scope.markets))
        && (requestedVenues.length ? requestedVenues.every(venue => canonicalExchangeScope(source.exchanges).includes(venue))
          : source.exchanges.length === 0)
        && (entry.data.oi_interval || entry.data.price_interval) === resolution
        && (!epoch || entry.data.oi_revision_epoch === epoch)
        && entry.range.from < range.to && entry.range.to >= range.from
        && (resolution !== '5s' || historyWindowCovers(entry.range, range))
        && (source.metrics === undefined || ((scope.metrics?.includes('oi') ?? true)
          ? source.metrics.includes('oi') : (scope.metrics || []).some(metric => source.metrics!.includes(metric))))
    })
    const covered = (entry: Entry) => (entry.data.oi_history_load?.completed || [{ from_ts: entry.range.from, to_ts: entry.range.to }])
      .reduce((sum, value) => sum + Math.max(0, Math.min(value.to_ts, range.to) - Math.max(value.from_ts, range.from)), 0)
    candidates.sort((a, b) => covered(b) - covered(a) || b.insertedAt - a.insertedAt)
    const entry = candidates[0]
    if (!entry) return null
    const metrics = scope.metrics || ['price', 'oi', 'volume', 'cvd', 'funding']
    const missingMetrics = metrics.filter(metric => (entry.reuseScope!.metrics !== undefined && !entry.reuseScope!.metrics.includes(metric))
      || (metric !== 'oi' && !historyWindowCovers(entry.range, range))
      || (metric === 'cvd' && entry.range.from !== range.from))
    return { data: rebaseScreenerHistory(entry.data, range), missingMetrics }
  }

  put(scope: string, data: ScreenerTickerData, requestedSpan: number, reuseScope?: ScreenerHistoryScope): void {
    const window = data.history_window
    if (!window || window.truncated || !Number.isFinite(window.from_ts) || !Number.isFinite(window.to_ts)) return
    const epoch = this.epochs.get(data.symbol.toUpperCase())
    const readAt = Math.max(data.read_started_at || 0, data.oi_read_started_at || 0)
    if (data.oi_revision_epoch && (!epoch || data.oi_revision_epoch === epoch.value || readAt > epoch.readAt)) this.observeEpoch(data)
    if (!this.acceptsEpoch(data)) return
    // Reconcile explicit range authorities in both directions: a narrower
    // correction updates a cached wide view, and a late wide snapshot cannot
    // restore a deletion already known to another compatible cache entry.
    const identity = data.oi_history_authorities || data.oi_history_load
    const fresh = !!identity && !this.appliedAuthorities.has(identity)
    if (identity) this.appliedAuthorities.add(identity)
    if (reuseScope) for (const entry of this.entries) {
      if (!entry.reuseScope) continue
      if (fresh) entry.data = mergeCachedAuthorities(entry.data, data, entry.reuseScope, reuseScope)
      data = mergeCachedAuthorities(data, entry.data, reuseScope, entry.reuseScope)
      Object.assign(entry, historyCost(entry.data))
    }
    const range = { from: window.from_ts, to: window.to_ts }
    const { points, bytes } = historyCost(data)
    if (points > this.limits.points || bytes > this.limits.bytes) { this.trim(); return }
    // Updating the same rolling buffer must not evict every historical visit.
    const tolerance = Math.max(1, (range.to - range.from) * 0.1)
    this.entries = this.entries.filter(entry => !(entry.scope === scope
      && entry.data.oi_interval === data.oi_interval
      && Math.abs(entry.range.from - range.from) <= tolerance
      && Math.abs(entry.range.to - range.to) <= tolerance))
    this.entries.unshift({ scope, data, range, requestedSpan, insertedAt: Date.now(), points, bytes,
      reuseScope: reuseScope ? { ...reuseScope, exchanges: canonicalExchangeScope(reuseScope.exchanges),
        markets: sorted(reuseScope.markets), metrics: reuseScope.metrics ? [...reuseScope.metrics] : undefined } : undefined })
    this.trim()
  }

  private trim(): void {
    while (this.entries.length > this.limits.windows
      || this.entries.reduce((sum, entry) => sum + entry.points, 0) > this.limits.points
      || this.entries.reduce((sum, entry) => sum + entry.bytes, 0) > this.limits.bytes) this.entries.pop()
  }
}
